import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/authStore';
import {
  deletePhoto,
  getPhotosForEntity,
  getSignedUrl,
  uploadPhoto,
  type PhotoAttachmentRow,
  type PhotoEntityType,
} from '@/services/db/photos';

const SIGNED_URL_REFRESH_MS = 4 * 60 * 1000; // 4 minutes
const DEFAULT_MAX_PHOTOS = 10;
const ACCEPT_ATTR = 'image/jpeg,image/png,image/heic,image/heif,image/webp';

interface Props {
  entityType: PhotoEntityType;
  entityId: string | null;
  dsrId: string;
  readOnly?: boolean;
  maxPhotos?: number;
}

interface SignedEntry {
  url: string;
  fetchedAt: number;
}

type ToastKind = 'error' | 'info';
interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

export default function PhotoUploader({
  entityType,
  entityId,
  dsrId,
  readOnly = false,
  maxPhotos = DEFAULT_MAX_PHOTOS,
}: Props) {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.tenantId);
  const userId = useAuthStore((s) => s.user?.id ?? null);

  const [signedUrls, setSignedUrls] = useState<Record<string, SignedEntry>>({});
  const [uploadQueue, setUploadQueue] = useState<{ total: number; current: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lightbox, setLightbox] = useState<{ url: string; name: string } | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toastIdRef = useRef(0);

  const queryKey = ['photos', entityType, entityId ?? 'unsaved'] as const;

  const { data: photos = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => getPhotosForEntity(entityType, entityId as string),
    enabled: !!entityId,
    staleTime: 30 * 1000,
  });

  const addToast = useCallback((kind: ToastKind, message: string) => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, kind, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const dismissToast = (id: number) => setToasts((prev) => prev.filter((t) => t.id !== id));

  // ─── Signed URL management ─────────────────────────────────────
  const fetchSignedUrl = useCallback(async (photo: PhotoAttachmentRow) => {
    const url = await getSignedUrl(photo.storage_path);
    if (url) {
      setSignedUrls((prev) => ({ ...prev, [photo.id]: { url, fetchedAt: Date.now() } }));
    }
    return url;
  }, []);

  useEffect(() => {
    const missing = photos.filter((p) => !signedUrls[p.id]);
    if (missing.length > 0) {
      Promise.all(missing.map(fetchSignedUrl));
    }
  }, [photos, signedUrls, fetchSignedUrl]);

  useEffect(() => {
    if (photos.length === 0) return;
    const timer = setInterval(() => {
      const now = Date.now();
      const stale = photos.filter((p) => {
        const entry = signedUrls[p.id];
        return !entry || now - entry.fetchedAt >= SIGNED_URL_REFRESH_MS;
      });
      if (stale.length > 0) {
        Promise.all(stale.map(fetchSignedUrl));
      }
    }, SIGNED_URL_REFRESH_MS);
    return () => clearInterval(timer);
  }, [photos, signedUrls, fetchSignedUrl]);

  // ─── Upload ─────────────────────────────────────────────────────
  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      if (!tenantId || !userId || !entityId) {
        throw new Error('Not authenticated');
      }
      return uploadPhoto({
        file,
        tenantId,
        dsrId,
        entityType,
        entityId,
        userId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (photoId: string) => deletePhoto(photoId),
    onSuccess: (_data, photoId) => {
      queryClient.invalidateQueries({ queryKey });
      setSignedUrls((prev) => {
        const next = { ...prev };
        delete next[photoId];
        return next;
      });
    },
    onError: (err) => {
      addToast('error', err instanceof Error ? err.message : 'Could not delete photo');
    },
  });

  const processFiles = useCallback(
    async (files: FileList | File[]) => {
      if (!entityId || readOnly) return;
      const list = Array.from(files);
      if (list.length === 0) return;

      const remainingSlots = maxPhotos - photos.length;
      const toUpload = list.slice(0, remainingSlots);
      if (list.length > remainingSlots) {
        addToast('error', `Only ${remainingSlots} more photo${remainingSlots === 1 ? '' : 's'} can be added`);
      }

      let uploadedCount = 0;
      for (let i = 0; i < toUpload.length; i++) {
        const file = toUpload[i];
        setUploadQueue({ total: toUpload.length, current: i + 1 });
        try {
          await uploadMutation.mutateAsync(file);
          uploadedCount++;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Upload failed. Try again.';
          addToast('error', `${file.name}: ${message}`);
        }
      }
      setUploadQueue(null);
      if (uploadedCount > 0) {
        // Ensure photos list is fresh
        queryClient.invalidateQueries({ queryKey });
      }
    },
    [entityId, readOnly, maxPhotos, photos.length, uploadMutation, addToast, queryClient, queryKey],
  );

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      processFiles(e.target.files);
      e.target.value = '';
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      processFiles(e.dataTransfer.files);
    }
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!readOnly && entityId) setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleThumbnailClick = async (photo: PhotoAttachmentRow) => {
    const existing = signedUrls[photo.id]?.url;
    const url = existing ?? (await fetchSignedUrl(photo));
    if (url) setLightbox({ url, name: photo.file_name });
  };

  const handleDelete = (photo: PhotoAttachmentRow) => {
    if (!confirm('Remove this photo?')) return;
    deleteMutation.mutate(photo.id);
  };

  // ─── Render ─────────────────────────────────────────────────────
  const atLimit = photos.length >= maxPhotos;
  const showUploadArea = !readOnly && !!entityId && !atLimit;

  // Read-only mode without any photos: render nothing (matches PhotoGrid)
  if (readOnly && photos.length === 0) return null;

  return (
    <div className="mt-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Photos {photos.length > 0 && `(${photos.length}/${maxPhotos})`}
        </p>
        {isLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </div>

      {/* Thumbnail grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {photos.map((photo) => {
            const entry = signedUrls[photo.id];
            return (
              <div key={photo.id} className="group relative aspect-square overflow-hidden rounded-md bg-muted">
                <button
                  type="button"
                  onClick={() => handleThumbnailClick(photo)}
                  className="h-full w-full transition hover:opacity-90"
                >
                  {entry?.url ? (
                    <img
                      src={entry.url}
                      alt={photo.file_name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </button>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => handleDelete(photo)}
                    disabled={deleteMutation.isPending}
                    className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition hover:bg-black/80 group-hover:opacity-100 disabled:opacity-50"
                    aria-label="Delete photo"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Not-yet-saved hint */}
      {!readOnly && !entityId && (
        <p className="rounded-md border border-dashed border-muted-foreground/40 py-4 text-center text-xs text-muted-foreground">
          Save this entry first before adding photos.
        </p>
      )}

      {/* Upload drop zone */}
      {showUploadArea && (
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          className={`mt-2 rounded-md border-2 border-dashed p-4 text-center transition ${
            isDragging
              ? 'border-mozaic-teal bg-mozaic-teal/5'
              : 'border-muted-foreground/30 hover:border-mozaic-teal/60'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            multiple
            className="hidden"
            onChange={onFileInputChange}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={!!uploadQueue}
            className="flex w-full flex-col items-center justify-center gap-1 text-xs text-muted-foreground hover:text-mozaic-teal disabled:opacity-50"
          >
            {uploadQueue ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>
                  Uploading {uploadQueue.current} of {uploadQueue.total}…
                </span>
              </>
            ) : (
              <>
                <ImagePlus className="h-5 w-5" />
                <span>Drop photos here or click to browse</span>
                <span className="text-[10px]">JPG, PNG, HEIC, WebP — max 10MB</span>
              </>
            )}
          </button>
        </div>
      )}

      {!readOnly && atLimit && (
        <p className="mt-2 text-xs text-muted-foreground">Maximum {maxPhotos} photos reached</p>
      )}

      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="mt-2 space-y-1">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`flex items-start justify-between gap-2 rounded-md border px-3 py-2 text-xs ${
                t.kind === 'error'
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : 'border-blue-200 bg-blue-50 text-blue-700'
              }`}
            >
              <span>{t.message}</span>
              <button
                type="button"
                onClick={() => dismissToast(t.id)}
                className="shrink-0 opacity-60 hover:opacity-100"
                aria-label="Dismiss"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-4 top-4 rounded-full bg-black/50 text-white hover:bg-black/70"
            onClick={() => setLightbox(null)}
          >
            <X className="h-4 w-4" />
          </Button>
          <div
            className="max-h-[90vh] max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.url}
              alt={lightbox.name}
              className="max-h-[85vh] max-w-[85vw] rounded-lg object-contain"
            />
            <p className="mt-2 text-center text-sm text-white/70">{lightbox.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}
